import type { OnboardingStepProps } from "./Onboarding";

import React from "react";

import { useShopifyIntegrationConnect } from "@editor/components/editor/page/useShopifyIntegrationConnect";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import twMerge from "@replo/design-system/utils/twMerge";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";
import useConnectShopifyIntegrationOnboarding from "./useConnectShopifyIntegrationOnboarding";

const ShopCard = ({
  onClick,
  isSelected,
  title,
  description,
  imageSrc,
  imageAlt,
  imageClassName,
}: {
  onClick: () => void;
  isSelected: boolean;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  imageClassName?: string;
}) => {
  return (
    <div
      className={twMerge(
        "md:aspect-[1.2] border-0.5 border-border group flex max-h-[33vh] md:max-h-[100vh] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-lg",
        isSelected
          ? "border-primary bg-selectable-selected"
          : "hover:bg-button-secondary-hover",
      )}
      onClick={onClick}
    >
      <div className="flex flex-col p-3">
        <span
          className={twMerge(
            "text-lg font-medium",
            isSelected ? "text-default" : "text-muted",
          )}
        >
          {title}
        </span>
        <span
          className={twMerge(
            "mb-2",
            isSelected ? "text-muted" : "text-placeholder",
          )}
        >
          {description}
        </span>
      </div>

      <img src={imageSrc} alt={imageAlt} className={twMerge(imageClassName)} />
    </div>
  );
};

const ConnectYourStore: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
}) => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { isChecking, isLoading: isLoadingShopifyIntegration } =
    useConnectShopifyIntegrationOnboarding({
      targetPath: "/onboarding/workspace-details",
    });

  const hasShopifyParams = React.useMemo(() => {
    const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");
    const pendingAppInstallationId = searchParams.get(
      "pendingAppInstallationId",
    );
    return Boolean(shopifyIntegrationId || pendingAppInstallationId);
  }, [searchParams]);

  const [selectedOption, setSelectedOption] = useLocalStorageState(
    "onboarding-setup-selected-option",
    hasShopifyParams ? "import" : null,
    {
      schema: z.union([z.literal("new"), z.literal("import"), z.null()]),
    },
  );

  React.useEffect(() => {
    if (
      hasShopifyParams &&
      selectedOption !== "import" &&
      selectedOption === null
    ) {
      setSelectedOption("import");
    }
  }, [hasShopifyParams, selectedOption, setSelectedOption]);

  const workspaceId = useCurrentWorkspaceId();
  const { handleConnect, isLoading: isConnectingShopify } =
    useShopifyIntegrationConnect({
      workspaceId,
      cookieValue: { type: "onboarding" },
    });

  const handleSubmit = (e: React.MouseEvent) => {
    logEvent("onboarding.flow.begin", {});
    if (selectedOption === "import") {
      void handleConnect(e);
    } else {
      if (user) {
        logEvent("onboarding.flow.step.completed", {
          userId: user.email,
          stepId: "setup",
          stepType: "setup",
          stepData: {
            selectedOption,
          },
        });
      }
      submitStep();
    }
  };

  const isSubmitDisabled =
    isLoading ||
    isChecking ||
    isLoadingShopifyIntegration ||
    !selectedOption ||
    (selectedOption === "import" && isConnectingShopify);

  return (
    <div className="flex h-screen w-full flex-col items-center overflow-y-auto">
      <div className="flex w-full flex-grow flex-col items-center overflow-y-auto md:py-12">
        <div className="flex w-full h-full flex-col items-center justify-between">
          <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-20 md:gap-3">
            <h1 className="header-h1 text-3xl md:text-4xl">
              Connect your Store
            </h1>
            <p className="text-muted header-h1 text-lg font-normal">
              Let's get you up and running!
            </p>
          </div>

          <div className="md:h-full md:max-w-[50vw] my-auto">
            <div className="flex w-full flex-col items-center gap-4 md:flex-row md:justify-center">
              <ShopCard
                onClick={() => setSelectedOption("import")}
                isSelected={selectedOption === "import"}
                title="Import Shop from Shopify"
                description="We'll pull your theme, products, and store info."
                imageSrc="/images/flows/integrate-shopify.svg"
                imageAlt="Integrate with Shopify"
              />
              <ShopCard
                onClick={() => setSelectedOption("new")}
                isSelected={selectedOption === "new"}
                title="I'm Brand New"
                description="Guide me through my store setup."
                imageSrc="/images/onboarding/new-shop.svg"
                imageAlt="New Shop"
                imageClassName={twMerge(
                  "transition-transform duration-300 ease-in-out",
                  selectedOption === "new"
                    ? "translate-y-0"
                    : "translate-y-2 group-hover:-translate-y-0",
                )}
              />
            </div>
          </div>

          <OnboardingFooter
            currentStep={currentStep}
            onNext={handleSubmit}
            isNextDisabled={isSubmitDisabled}
            className="w-full md:max-w-[50vw]"
            isNextLoading={isConnectingShopify || isLoadingShopifyIntegration}
          />
        </div>
      </div>
    </div>
  );
};

export default ConnectYourStore;
